import { Layout, Table, Typography, Spin } from "antd";
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react";
import axios from "api/axios";
import {
  calculatePercentage,
  countApplicationsForEachDomain,
  formatDataGeneral,
  getAttributeValue,
  getFileLinkIfExist,
} from "utils/utils";
import { renderDpatEvidenceLink, scoreOnlyIfDocumentLinksComplete } from "utils/renderDpatEvidenceLink";
import { documentsColumn } from "utils/tableColums";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import MaxScoreDisplay from "../setup/MaxScoreDisplay";
import ActualScoreDisplay from "../setup/ActualScoreDisplay";
import IndicatorScoreSection from "../setup/IndicatorScoreSection";
import Comment from "components/Comments";
import { hydrateSdi2ServiceProviderSnapshot } from "utils/dpatAssessmentStartData";
import { useDpatIndicatorDataSource } from "hooks/useDpatIndicatorDataSource";

const DOCUMENT_HUB_FILE_ELEMENT = "MjPmcbueEdU";

function fileUrl(eventId, dataElementUid) {
  return `https://dddp.gov.gh/api/events/files?eventUid=${eventId}&dataElementUid=${dataElementUid}`;
}

function buildElectricityServiceProviderDocLinks(documents) {
  const documentData = documents?.data;
  const documentReports = documents?.reports;
  const electricityServiceDocs = formatDataGeneral(
    documentData,
    "DPAT | M&E Tool Type",
    "Electricity Service Provider",
  ) || [];

  let electricityServiceProviderLink = "";
  if (electricityServiceDocs.length > 0) {
    electricityServiceProviderLink = getFileLinkIfExist(
      documentReports,
      DOCUMENT_HUB_FILE_ELEMENT,
      electricityServiceDocs[0]?.trackedEntity,
    );
  }

  return [
    {
      title: "Electricity Service Provider",
      link: electricityServiceProviderLink
        ? fileUrl(electricityServiceProviderLink, DOCUMENT_HUB_FILE_ELEMENT)
        : "Not Uploaded",
    },
  ];
}

const ElectricityServices = forwardRef(({
  year,
  districtId,
  documents,
  hideComment,
  savedAssessmentData,
  assessmentStartLoading,
  isAssessmentStarted,
}, ref) => {
  const { Content } = Layout;
  const { Title } = Typography;

  const [scorei, setScoreI] = useState(0);
  const [scoreii, setScoreII] = useState(0);
  const [waterProvidersData, setWaterProvidersData] = useState([]);
  const [waterImprovement, setWaterImpovement] = useState([]);
  const [documentLinks, setDocumentLinks] = useState([]);
  const [firstPercentage, setFirstPercentage] = useState(0.00);
  const [maxScore, setMaxScore] = useState(3);

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "SDI2",
      area: "Basic/ Social Services",
      maxScore,
      waterProvidersData,
      waterImprovement,
      links: documentLinks,
      scorei,
      scoreii,
      firstPercentage,
    }),
  }));

  const serviceProvidersColumn = [
    { title: "No", dataIndex: "no", key: "no" },
    { title: "Service Provider", dataIndex: "provider", key: "provider" },
    { title: "Start Date", dataIndex: "date", key: "date" },
  ];

  const serviceProvidersImprovementColumn = [
    { title: "Total no. of Applications for electricity service (A)", dataIndex: "application", key: "application" },
    { title: "Total no. of applications processed (B)", dataIndex: "processed", key: "processed" },
    { title: "Total No. of Applications provided electricity service (C)", dataIndex: "provided", key: "provided" },
    {
      title: "% of applications processed (B/A x 100)",
      dataIndex: "percentage",
      key: "percentage",
    },
  ];

  const documentTableColumns = useMemo(
    () => documentsColumn.map((col) =>
      col.dataIndex === "link"
        ? {
          ...col,
          render: (value) => renderDpatEvidenceLink(value, "View Document"),
        }
        : col
    ),
    [],
  );

  const getServiceProviders = useCallback(() => {
    const links = buildElectricityServiceProviderDocLinks(documents);
    setDocumentLinks(links);

    axios
      .get(`/tracker/trackedEntities?orgUnit=${districtId}&program=nGFVo65uUE4`)
      .then((result) => {
        if (result.data.instances.length === 0) {
          setWaterProvidersData([]);
          setWaterImpovement([]);
          setFirstPercentage("0.00");
          setScoreI(0);
          setScoreII(0);
          return;
        }

        axios
          .get(`/tracker/events?program=nGFVo65uUE4&orgUnit=${districtId}&startDate=${year}-01-01&endDate=${year}-12-31`)
          .then((resp) => {
            const serviceProviders = result.data.instances;
            const reports = resp.data.instances;
            const temp = [];

            const electricityProviders =
              formatDataGeneral(serviceProviders, "DPAT | Service Provider", "Electricity Service Provider") || [];

            electricityProviders.forEach((service, index) => {
              temp.push({
                key: index + 1,
                no: index + 1,
                provider: getAttributeValue("Name of Business", service),
                trackedEntity: service.trackedEntity,
                date: getAttributeValue("Start Date", service),
              });
            });

            const electricityCounters = countApplicationsForEachDomain(temp, reports);
            const percentageII = calculatePercentage(electricityCounters[1], electricityCounters[0]);
            const percentageDisplay = Number(percentageII).toFixed(2);

            setWaterProvidersData(temp);
            setWaterImpovement([
              {
                application: electricityCounters[0],
                processed: electricityCounters[1],
                provided: electricityCounters[2],
                percentage: percentageDisplay,
              },
            ]);
            setFirstPercentage(percentageDisplay);
            setScoreI(scoreOnlyIfDocumentLinksComplete(links, temp.length > 0 ? 1 : 0));
            setScoreII(scoreOnlyIfDocumentLinksComplete(links, percentageII >= 80 ? 2 : 0));
          })
          .catch((err) => console.log(err));
      })
      .catch((err) => console.log(err));
  }, [districtId, year, documents]);

  const { loading } = useDpatIndicatorDataSource({
    isAssessmentStarted,
    savedAssessmentData,
    assessmentStartLoading,
    loadFromDhis2: getServiceProviders,
    applySnapshot: (snap) =>
      hydrateSdi2ServiceProviderSnapshot(snap, {
        setProviders: setWaterProvidersData,
        setImprovement: setWaterImpovement,
        setLinks: setDocumentLinks,
        setScoreI,
        setScoreII,
        setFirstPercentage,
        setMaxScore,
      }),
    deps: [districtId, year, documents, getServiceProviders],
  });

  const DPAT_SLUG = "sdi2.0-2.2";

  return (
    <Spin spinning={loading} tip={loading ? "Loading saved assessment data…" : undefined}>
      <Comment
        data={waterProvidersData}
        year={year}
        districtId={districtId}
        tableCommentedId={`sdi2.0-2.2-${year}`}
        hideComment={hideComment}
      >
        {({ renderCommentFeedbackFooter }) => (
          <DpatSubIndicatorSection code={DPAT_SLUG}>
            <DpatIndicatorCmsIntro
              year={year}
              slug={DPAT_SLUG}
              mainTitleStyle={{ marginTop: "30px" }}
            />

            <IndicatorScoreSection>
              <MaxScoreDisplay score={maxScore} />
              <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 2.0-2.2i Actual Score" score={scorei} />
              <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 2.0-2.2ii Actual Score" score={scoreii} />
            </IndicatorScoreSection>

            <Title level={5} style={{ marginTop: "30px" }}>I. Evidence of existing collaboration/facilitation electricity service providers</Title>
            {waterProvidersData?.length > 0 && (
              <Table
                columns={serviceProvidersColumn}
                dataSource={waterProvidersData}
                rowKey="key"
                pagination={false}
                bordered
              />
            )}

            <Title level={5} style={{ marginTop: "20px" }}>Evidence of collaboration (Document Hub)</Title>
            <Table
              columns={documentTableColumns}
              dataSource={documentLinks}
              rowKey="title"
              pagination={false}
              bordered
            />

            <Title level={5} style={{ marginTop: "30px" }}>II. Evidence of increased access to electricity due to collaboration/facilitation</Title>
            {waterImprovement?.length > 0 && (
              <Table
                columns={serviceProvidersImprovementColumn}
                dataSource={waterImprovement}
                rowKey="application"
                pagination={false}
                bordered
              />
            )}

            <Title level={5} style={{ marginTop: "30px" }}>Conclusion:</Title>
            <Content>
              <ol>
                <li type="i">
                  Evidence of collaboration/ facilitation between the Assembly and
                  CWSA in the district {waterProvidersData.length > 0 ? "exist" : "does not exist"}
                </li>
                <li className="py-1" type="i">
                  {firstPercentage} % of applications for Electricity Services in {year} have been
                  processed as a result of the collaboration and facilitation.
                </li>
              </ol>
            </Content>
            {!hideComment && renderCommentFeedbackFooter()}
          </DpatSubIndicatorSection>
        )}
      </Comment>
    </Spin>
  );
});

export default ElectricityServices;
